import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Trash2, ArrowLeft, Lock, Check, X, ExternalLink,
  AlertTriangle, RefreshCw, Clock, Sparkles, Pencil,
} from "lucide-react";

// ── Inline editor for an event (fix a flagged date, venue, etc.) ─────────
function EventEditor({ item, headers, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: item.name || item.title || "",
    date: item.date || "",
    time: item.time || "",
    venue: item.venue || "",
    city: item.city || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date)) { toast.error("Date must be YYYY-MM-DD"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/events/${item.id}`, form, { headers });
      toast.success("Saved — flag cleared");
      onSaved(data.event);
    } catch (e) { toast.error("Couldn't save", { description: e?.response?.data?.detail }); }
    finally { setSaving(false); }
  };

  const field = "h-9 text-sm";
  return (
    <div className="mt-3 p-3 rounded-xl bg-muted/50 border border-border grid gap-2 sm:grid-cols-2">
      <Input className={`${field} sm:col-span-2`} placeholder="Title" value={form.name} onChange={e => set("name", e.target.value)} />
      <Input className={field} placeholder="Date (YYYY-MM-DD)" value={form.date} onChange={e => set("date", e.target.value)} autoFocus />
      <Input className={field} placeholder="Time (e.g. 6:30 PM)" value={form.time} onChange={e => set("time", e.target.value)} />
      <Input className={field} placeholder="Venue" value={form.venue} onChange={e => set("venue", e.target.value)} />
      <Input className={field} placeholder="City" value={form.city} onChange={e => set("city", e.target.value)} />
      <div className="sm:col-span-2 flex gap-2 justify-end pt-1">
        <Button size="sm" variant="outline" className="rounded-full" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="rounded-full" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Save</>}
        </Button>
      </div>
    </div>
  );
}

const COLLECTIONS = ["posts", "events", "housing", "jobs", "businesses", "feedback"];
const STATUSES = ["pending", "live", "rejected", "all"];

// collection → backend path used by approve/reject
const MODERATION_PATH = { posts: "posts", events: "events", housing: "accommodations", businesses: "businesses", jobs: "jobs" };

const SOURCE_STYLE = {
  ticketmaster: { bg: "#E6F1FB", fg: "#0C447C" },
  greenhouse:   { bg: "#E6F1FB", fg: "#0C447C" },
  lever:        { bg: "#E6F1FB", fg: "#0C447C" },
  workday:      { bg: "#E6F1FB", fg: "#0C447C" },
  usajobs:      { bg: "#E1F5EE", fg: "#085041" },
  adzuna:       { bg: "#EEEDFE", fg: "#3C3489" },
  page:         { bg: "#EEEDFE", fg: "#3C3489" },
  pdf:          { bg: "#EEEDFE", fg: "#3C3489" },
  ics:          { bg: "#E1F5EE", fg: "#085041" },
  curated:      { bg: "#EAF3DE", fg: "#27500A" },
  manual:       { bg: "#F1EFE8", fg: "#444441" },
};

function Badge({ bg, fg, children }) {
  return (
    <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: bg, color: fg }}>
      {children}
    </span>
  );
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// ── Import panel (Events and Jobs tabs) ───────────────────────────────────
const PANELS = {
  events: {
    title: "Event import", statusPath: "/admin/ingest/status", runPath: "/admin/ingest/run",
    summary: (last) => `${last.added} new · ${last.skipped} skipped${last.errors ? ` · ${last.errors} errors` : ""}${last.ai_calls != null ? ` · ${last.ai_calls} AI call${last.ai_calls === 1 ? "" : "s"}` : ""}`,
    badges: (cfg) => [!cfg.anthropic && ["AI extraction off", "#F1EFE8", "#444441"], !cfg.ticketmaster && ["Ticketmaster key missing", "#FAEEDA", "#633806"]],
    canRun: (cfg) => cfg.anthropic || cfg.ticketmaster,
    toast: (d) => `Import finished — ${d.added} new, ${d.skipped} skipped`,
    extra: [{ label: "Fetch photos", path: "/admin/events/fetch-photos", toast: (d) => `Photos: ${d.found} found, ${d.missed} had none (checked ${d.checked})` }],
  },
  jobs: {
    title: "Jobs import", statusPath: "/admin/jobs/ingest/status", runPath: "/admin/jobs/ingest/run",
    summary: (last) => `${last.added} new · ${last.refreshed ?? 0} refreshed · ${last.expired} expired${last.errors ? ` · ${last.errors} errors` : ""}`,
    badges: (cfg) => [!cfg.adzuna && ["Adzuna keys missing", "#FAEEDA", "#633806"], !cfg.usajobs && ["USAJOBS key missing", "#FAEEDA", "#633806"], !cfg.sponsors_loaded && ["Sponsor data not loaded", "#FAECE7", "#712B13"]],
    canRun: () => true,
    toast: (d) => `Jobs import finished — ${d.added} new, ${d.refreshed} refreshed, ${d.expired} expired`,
    extra: [{ label: "Discover employers", path: "/admin/jobs/discover?top=40", needs: "sponsors_loaded",
              toast: (d) => `Checked ${d.checked} sponsors — found ${d.found} job feed${d.found === 1 ? "" : "s"}` }],
  },
};

function ExtraButton({ btn, headers, cfg, onDone }) {
  const [busy, setBusy] = useState(false);
  const blocked = btn.needs && !cfg[btn.needs];
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(btn.path, {}, { headers, timeout: 600000 });
      toast.success(btn.toast(data));
      onDone?.();
    } catch (e) { toast.error(`${btn.label} failed`, { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="rounded-full" onClick={run} disabled={busy || blocked}
      title={blocked ? "Load the sponsor data first (scripts/build_sponsors.py)" : undefined}>
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{busy ? "Working…" : btn.label}
    </Button>
  );
}

function ImportPanel({ kind = "events", headers, onImported }) {
  const P = PANELS[kind];
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get(P.statusPath, { headers }).then(({ data }) => setStatus(data)).catch(() => {});
  }, [headers, P.statusPath]);

  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(P.runPath, {}, { headers, timeout: 180000 });
      toast.success(P.toast(data), { description: data.errors ? `${data.errors} source(s) had errors — see details below.` : undefined });
      load();
      onImported?.();
    } catch (e) {
      toast.error("Import failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  const last = status?.last_run;
  const cfg = status?.configured || {};
  const counts = status?.counts;

  return (
    <div className="mb-6 rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4" /></div>
          <div className="min-w-0">
            <p className="font-medium text-foreground">{P.title}{counts ? <span className="text-xs font-normal text-muted-foreground ml-2">{counts.live} live · {counts.pending} pending · {counts.expired} expired{status?.discovered_feeds ? ` · ${status.discovered_feeds} discovered feeds` : ""}</span> : null}</p>
            <p className="text-xs text-muted-foreground">
              {last ? <>Last run {fmtWhen(last.finished_at || last.started_at)} · {P.summary(last)}</> : "Never run. Runs every Monday 6 am once the scheduler is set up."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {P.badges(cfg).filter(Boolean).map(([t, bg, fg]) => <Badge key={t} bg={bg} fg={fg}>{t}</Badge>)}
          {(P.extra || []).map(b => <ExtraButton key={b.label} btn={b} headers={headers} cfg={cfg} onDone={() => { load(); onImported?.(); }} />)}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(o => !o)}>{open ? "Hide details" : "Details"}</Button>
          <Button size="sm" className="rounded-full" onClick={run} disabled={running || !P.canRun(cfg)}>
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {running ? "Importing…" : "Run import now"}
          </Button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border p-4 space-y-2">
          {(last?.sources || status?.sources || []).map(s => (
            <div key={s.id} className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0">
                <p className="text-foreground truncate">{s.name} <span className="text-xs text-muted-foreground">· {s.kind}</span></p>
                {s.note && <p className={`text-xs mt-0.5 ${s.cached ? "text-muted-foreground" : "text-amber-700"}`}>{s.note}</p>}
                {s.error && <p className="text-xs text-destructive mt-0.5">{s.error}</p>}
              </div>
              {"added" in s && (
                <p className="text-xs text-muted-foreground shrink-0">{s.found ?? 0} found · <span className="text-foreground">{s.added} new</span> · {s.refreshed != null ? `${s.refreshed} refreshed` : `${s.skipped} skipped`}</p>
              )}
            </div>
          ))}
          {!last && <p className="text-xs text-muted-foreground">Sources are listed in <code>backend/{kind === "jobs" ? "jobs_ingest/employers.py" : "ingest/sources.py"}</code>. Run once to see per-source results.</p>}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("posts");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busy, setBusy] = useState({});   // id → true while a moderation call is in flight
  const [editing, setEditing] = useState(null);   // event id currently open in the editor

  const applyEdit = (updated) => {
    setData(prev => ({ ...prev, events: prev.events.map(e => e.id === updated.id ? { ...e, ...updated } : e) }));
    setEditing(null);
  };

  const headers = { "X-Admin-Passcode": passcode };

  const reload = useCallback(async () => {
    const { data: all } = await api.get("/admin/all", { headers: { "X-Admin-Passcode": passcode } });
    setData(all);
  }, [passcode]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", {}, { headers });
      await reload();
      setAuthed(true);
    } catch { toast.error("Wrong passcode"); }
    finally { setLoading(false); }
  };

  const moderate = async (collection, id, action) => {
    const path = MODERATION_PATH[collection];
    if (!path) return;
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/${path}/${id}/${action}`, {}, { headers });
      setData(prev => ({
        ...prev,
        [collection]: prev[collection].map(i => i.id === id ? { ...i, status: action === "approve" ? "live" : "rejected" } : i),
      }));
      toast.success(action === "approve" ? "Approved — it's live" : "Rejected");
    } catch { toast.error(`Couldn't ${action}`); }
    finally { setBusy(b => ({ ...b, [id]: false })); }
  };

  const del = async (collection, id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await api.delete(`/admin/${collection}/${id}`, { headers });
      setData(prev => ({ ...prev, [collection]: prev[collection].filter(i => i.id !== id) }));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (!authed) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm p-8 rounded-2xl border border-border bg-card shadow-lg">
        <div className="flex items-center gap-3 mb-6"><div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg font-semibold">N</div><h1 className="font-serif text-2xl font-semibold">NC Desi Admin</h1></div>
        <form onSubmit={login} className="space-y-4">
          <Input type="password" placeholder="Admin passcode" value={passcode} onChange={e => setPasscode(e.target.value)} className="h-12" autoFocus />
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-full">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Enter Admin</>}</Button>
        </form>
        <Link to="/" className="mt-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" />Back to site</Link>
      </div>
    </div>
  );

  const all = data?.[tab] || [];
  const hasStatus = all.some(i => "status" in i);
  const items = hasStatus && statusFilter !== "all" ? all.filter(i => (i.status || "live") === statusFilter) : all;
  const pendingCount = (c) => (data?.[c] || []).filter(i => i.status === "pending").length;
  const getTitle = (item) => item.title || item.name || item.message?.slice(0, 40) || item.id;
  const canModerate = !!MODERATION_PATH[tab];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-serif font-semibold">N</div><h1 className="font-serif text-2xl font-semibold">NC Desi Admin</h1></div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => reload().then(() => toast.success("Refreshed"))}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
            <Link to="/"><Button variant="outline" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4 mr-2" />Back to site</Button></Link>
          </div>
        </div>

        {/* Collection tabs — pending counts in red so the queue is visible at a glance */}
        <div className="flex flex-wrap gap-2 mb-4">
          {COLLECTIONS.map(c => {
            const pc = pendingCount(c);
            return (
              <button key={c} onClick={() => { setTab(c); setStatusFilter(MODERATION_PATH[c] ? "pending" : "all"); }}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors border ${tab === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                {c}
                {pc > 0
                  ? <span className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full ${tab === c ? "bg-white/25" : "bg-destructive text-white"}`}>{pc}</span>
                  : <span className="ml-1 text-xs opacity-60">({(data?.[c] || []).length})</span>}
              </button>
            );
          })}
        </div>

        {/* Status filter */}
        {hasStatus && (
          <div className="flex items-center gap-1 mb-6">
            {STATUSES.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded-lg text-[13px] font-medium capitalize transition-colors ${statusFilter === s ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        {tab === "events" && <ImportPanel kind="events" headers={headers} onImported={reload} />}
        {tab === "jobs" && <ImportPanel kind="jobs" headers={headers} onImported={reload} />}

        {items.length === 0
          ? <p className="text-muted-foreground py-12 text-center">
              {statusFilter === "pending" && hasStatus ? "Nothing waiting for review." : `No ${tab} here.`}
            </p>
          : <div className="space-y-3">{items.map(item => {
              const src = item.source || "manual";
              const ss = SOURCE_STYLE[src] || SOURCE_STYLE.manual;
              const lowConf = item.confidence === "low";
              return (
                <div key={item.id} className={`flex items-start gap-4 p-4 rounded-xl border bg-card ${lowConf ? "border-amber-300" : "border-border"}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {tab === "events" && (
                        src === "manual"
                          ? <Badge bg={ss.bg} fg={ss.fg}>Submitted by a member</Badge>
                          : <Badge bg={ss.bg} fg={ss.fg}>{src === "curated" ? "Curated" : "Auto"} · {item.source_name || src}</Badge>
                      )}
                      {tab === "events" && src !== "manual" && (
                        lowConf
                          ? <Badge bg="#FAECE7" fg="#712B13"><AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />Check the date</Badge>
                          : <Badge bg="#EAF3DE" fg="#27500A">Date confirmed</Badge>
                      )}
                      {tab === "jobs" && (
                        <>
                          <Badge bg={ss.bg} fg={ss.fg}>{src === "manual" ? `Member${item.can_refer ? " · can refer" : ""}` : `Auto · ${item.source_name || src}`}</Badge>
                          {item.sponsors_h1b === "yes" && <Badge bg="#EAF3DE" fg="#27500A">H-1B ✓</Badge>}
                          {item.sponsors_gc === "yes" && <Badge bg="#EAF3DE" fg="#27500A">Green card ✓</Badge>}
                          {item.accepts_opt === "yes" && <Badge bg="#EAF3DE" fg="#27500A">STEM OPT ✓</Badge>}
                          {item.accepts_opt === "likely" && <Badge bg="#FAEEDA" fg="#633806">STEM OPT likely</Badge>}
                          {item.sponsors_h1b === "no" && <Badge bg="#F1EFE8" fg="#444441">No sponsorship</Badge>}
                        </>
                      )}
                      {item.status && item.status !== "pending" && (
                        <Badge bg={item.status === "live" ? "#EAF3DE" : "#F1EFE8"} fg={item.status === "live" ? "#27500A" : "#444441"}>{item.status}</Badge>
                      )}
                    </div>
                    <p className="font-medium text-foreground truncate">{getTitle(item)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {tab === "events" && item.date ? `${item.date}${item.time ? ` · ${item.time}` : ""} · ` : ""}
                      {[item.venue, item.city].filter(Boolean).join(", ") || item.type || item.category || item.cat || ""}
                      {item.created_at ? ` · added ${new Date(item.created_at).toLocaleDateString()}` : ""}
                      {item.source_url && <> · <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">source page <ExternalLink className="w-3 h-3" /></a></>}
                    </p>
                    {lowConf && item.confidence_note && (
                      <p className="text-xs text-amber-800 mt-1.5 bg-amber-50 rounded-lg px-2.5 py-1.5 inline-block"><Clock className="w-3 h-3 inline -mt-0.5 mr-1" />{item.confidence_note}</p>
                    )}
                    {(item.body || item.desc || item.description || item.message) && (
                      <p className="text-sm text-foreground/70 mt-1 line-clamp-2">{item.body || item.desc || item.description || item.message}</p>
                    )}
                    {tab === "events" && editing === item.id && (
                      <EventEditor item={item} headers={headers} onSaved={applyEdit} onCancel={() => setEditing(null)} />
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {tab === "events" && item.status !== "rejected" && editing !== item.id && (
                      <Button size="sm" variant={lowConf ? "default" : "outline"} className="rounded-full" onClick={() => setEditing(item.id)}>
                        <Pencil className="w-4 h-4 mr-1" />{lowConf ? "Fix date" : "Edit"}
                      </Button>
                    )}
                    {canModerate && item.status === "pending" && (
                      <>
                        <Button size="sm" className="rounded-full" disabled={busy[item.id]} onClick={() => moderate(tab, item.id, "approve")}>
                          {busy[item.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Approve</>}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" disabled={busy[item.id]} onClick={() => moderate(tab, item.id, "reject")}>
                          <X className="w-4 h-4 mr-1" />Reject
                        </Button>
                      </>
                    )}
                    {canModerate && item.status === "rejected" && (
                      <Button size="sm" variant="outline" className="rounded-full" disabled={busy[item.id]} onClick={() => moderate(tab, item.id, "approve")}>
                        <Check className="w-4 h-4 mr-1" />Approve anyway
                      </Button>
                    )}
                    <Button variant="outline" size="icon" className="text-destructive hover:bg-destructive hover:text-white border-destructive/30" onClick={() => del(tab, item.id)} aria-label="Delete"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            })}</div>}
      </div>
    </div>
  );
}
